// Lista de contratos, del más nuevo al más viejo.
//
// Vive como sub-pantalla de Precios (no es una octava solapa): se llega por el
// conmutador de arriba o generando un contrato desde un presupuesto aceptado.

import { api } from "../api";
import { esc } from "../html";
import { iconoMas } from "../iconos";
import { ETIQUETA_ESTADO_CONTRATO, claseEstadoContrato } from "../etiquetas";
import type { Cliente, Contrato } from "../../lib/tipos";
import { formatearMonto } from "../../lib/calculo";
import { formatoLocal } from "../../lib/fecha";

/** El conmutador Presupuestos | Contratos. Lo comparten las dos listas. */
export function conmutadorPrecios(activo: "presupuestos" | "contratos"): string {
  const base = "flex-1 rounded-lg px-3 py-2 text-center text-sm font-medium transition";
  const on = "bg-realce text-texto";
  const off = "text-apagado hover:text-texto";
  return `
    <div class="mt-3 flex gap-1 rounded-xl border border-borde bg-superficie p-1">
      <a href="#/presupuestos" class="${base} ${activo === "presupuestos" ? on : off}">Presupuestos</a>
      <a href="#/contratos" class="${base} ${activo === "contratos" ? on : off}">Contratos</a>
    </div>`;
}

function fila(c: Contrato): string {
  const apagado = c.estado === "ANULADO" ? "opacity-60" : "";
  return `
    <a href="#/contrato/${c.id}" class="fila block p-4 ${apagado}">
      <div class="flex items-start gap-2">
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-2">
            <span class="font-mono text-sm text-apagado">Nº ${c.numero}</span>
            <span class="truncate font-medium">${esc(c.cliente_nombre)}</span>
          </div>
          <div class="mt-0.5 truncate text-xs text-apagado">
            ${formatoLocal(c.fecha)}${c.proyecto_nombre ? " · " + esc(c.proyecto_nombre) : ""}${
              c.presupuesto_numero ? ` · del presupuesto Nº ${c.presupuesto_numero}` : ""
            }
          </div>
        </div>
        <span class="${claseEstadoContrato(c.estado)}">${ETIQUETA_ESTADO_CONTRATO[c.estado]}</span>
      </div>
      <div class="mt-2.5 flex items-center justify-between gap-2">
        <span class="text-lg font-semibold">${formatearMonto(c.monto, c.moneda)}</span>
        <span class="text-xs text-apagado">${c.clausulas} ${
          c.clausulas === 1 ? "cláusula" : "cláusulas"
        }</span>
      </div>
    </a>`;
}

export async function renderContratos(contenedor: HTMLElement): Promise<void> {
  async function pintar(): Promise<void> {
    const [contratos, clientes] = await Promise.all([
      api.get<Contrato[]>("/api/contratos"),
      api.get<Cliente[]>("/api/clientes"),
    ]);

    contenedor.innerHTML = `
      <h1 class="titulo-pagina">Contratos</h1>
      <p class="subtitulo">${contratos.length} en total</p>
      ${conmutadorPrecios("contratos")}

      <div class="mt-4 space-y-2">
        ${
          contratos.length
            ? contratos.map(fila).join("")
            : `<div class="tarjeta text-center text-apagado">
                 Todavía no hay contratos. El camino normal es generar uno desde
                 un <a href="#/presupuestos" class="text-acento hover:underline">presupuesto aceptado</a>.
               </div>`
        }
      </div>

      ${
        clientes.length
          ? `<details class="tarjeta mt-4">
        <summary class="flex items-center gap-2 font-medium text-acento">${iconoMas} Contrato suelto (sin presupuesto)</summary>
        <form id="fNuevo" class="mt-4 space-y-3">
          <label><span class="etiqueta">Cliente</span>
            <select id="cliente" class="campo" required>
              ${clientes.map((c) => `<option value="${c.id}">${esc(c.nombre)}</option>`).join("")}
            </select></label>
          <p id="errNuevo" class="text-sm text-alerta"></p>
          <button class="btn-primario w-full">Crear borrador</button>
        </form>
      </details>`
          : ""
      }`;

    const form = contenedor.querySelector("#fNuevo") as HTMLFormElement | null;
    form?.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errNuevo") as HTMLElement;
      err.textContent = "";
      try {
        const creado = await api.post<Contrato>("/api/contratos", {
          cliente_id: Number((contenedor.querySelector("#cliente") as HTMLSelectElement).value),
        });
        location.hash = `#/contrato/${creado.id}`;
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo crear";
      }
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudieron cargar los contratos: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
